// Render status as returned by the backend (`crates/db/src/models/render.rs`).
export type RenderStatus =
  | "queued"
  | "rendering"
  | "complete"
  | "error";

export type OutputFormat = "mp4" | "webm" | "prores" | "gif" | "h265" | "mp4-matte";

export type ProResProfile =
  | "4444-xq"
  | "4444"
  | "hq"
  | "standard"
  | "light"
  | "proxy";

export interface RenderConfig {
  template_id: string;
  props: Record<string, unknown>;
  format: OutputFormat;
  prores_profile?: ProResProfile;
  width?: number;
  height?: number;
  fps?: number;
  duration_frames?: number;
  /// Constant Rate Factor (0–51 for h264/h265, 0–63 for vp9). Lower is better.
  crf?: number;
  /// Content scale (0.3-1.0). Shrinks the graphic to fit overflowing content.
  content_scale?: number;
  /// Anti-banding dither for ProRes alpha profiles. Only sent for
  /// 4444 / 4444-xq so other formats keep their dedup hashes.
  dither?: boolean;
}

/** UI-side render settings — everything except template_id and props. */
export interface RenderSettings {
  format: OutputFormat;
  prores_profile?: ProResProfile;
  width: number;
  height: number;
  fps: number;
  duration_frames: number;
  /** When true (the default), duration_frames is ignored and the render
   *  follows the duration resolved from the template's timing fields —
   *  so a 5-minute countdown renders 5 minutes without any manual setup.
   *  Set false only by an explicit duration edit in the render controls. */
  duration_auto?: boolean;
  crf?: number;
  /** Content scale applied to the whole graphic (0.3-1.0). Use to shrink
   *  overflowing content to fit within the composition. */
  content_scale?: number;
  /** Anti-banding dither on ProRes alpha renders (default true). Chrome's
   *  8-bit frames quantize wide soft glows into visible contour rings;
   *  this masks them with ±2-level noise on partially-transparent pixels. */
  dither?: boolean;
}

export const DEFAULT_RENDER_SETTINGS: RenderSettings = {
  format: "mp4",
  width: 1920,
  height: 1080,
  fps: 30,
  duration_frames: 150,
  duration_auto: true,
  crf: 23,
  content_scale: 1.0,
};

/// Mirrors the backend `Render` struct.
export interface RenderJob {
  id: string;
  template_id: string;
  user_id: string;
  props: string;
  status: RenderStatus;
  output_path: string | null;
  output_size: number | null;
  format: OutputFormat;
  error_message: string | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  template_version: number;
  props_hash: string | null;
  progress: number;
  /// Optional client-side enrichment (not returned by the backend list endpoint).
  template_name?: string;
}

/** Mirrors the backend's ListParams — limit/offset only (default limit 20). */
export interface RenderListParams {
  status?: RenderStatus;
  template_id?: string;
  limit?: number;
  offset?: number;
}
